import React, { useState, useMemo, useEffect } from "react";
import { Calculator, TrendingUp, TrendingDown, Percent, Euro, Gauge, Wallet, Save, FolderOpen, Trash2, Plus, X, HelpCircle, LineChart as LineChartIcon, FileDown, Loader2 } from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine, ComposedChart, Line, Legend } from "recharts";
import api from "../../lib/api";
import { toast } from "sonner";
import { useLocation } from "react-router-dom";

const N = (v) => Number(v) || 0;
const eur = (v) => `${Math.round(N(v)).toLocaleString("fr-FR")} €`;
const pct = (v) => `${(N(v)).toFixed(2)} %`;

const Field = ({ label, value, onChange, suffix = "€" }) => (
  <label className="block">
    <span className="text-xs font-medium text-brand-green/60">{label}</span>
    <div className="flex items-center gap-2 mt-1 px-3 py-2 rounded-xl border border-brand-soft bg-white focus-within:border-brand-lime">
      <input type="number" value={value} onChange={(e) => onChange(e.target.value)} className="w-full outline-none text-sm text-brand-greenDeep bg-transparent" />
      <span className="text-xs text-brand-green/40">{suffix}</span>
    </div>
  </label>
);

const Kpi = ({ label, value, tone = "neutral", icon: Icon, hint, formula }) => {
  const c = tone === "up" ? "text-emerald-600" : tone === "down" ? "text-red-600" : "text-brand-greenDeep";
  return (
    <div className="bg-white rounded-2xl border border-brand-soft p-4" data-testid={`kpi-${label}`}>
      <div className="flex items-center gap-1.5 text-xs text-brand-green/50">
        {Icon && <Icon className="w-3.5 h-3.5" />}{label}
        {formula && (
          <span className="relative group ml-auto" data-testid={`kpi-help-${label}`}>
            <HelpCircle className="w-3.5 h-3.5 text-brand-green/30 hover:text-brand-greenDeep cursor-help" />
            <span className="pointer-events-none absolute right-0 top-5 z-30 w-56 rounded-lg bg-brand-greenDeep text-white text-[11px] leading-snug px-3 py-2 opacity-0 group-hover:opacity-100 transition-opacity shadow-lg">
              {formula}
            </span>
          </span>
        )}
      </div>
      <p className={`font-display text-xl font-bold mt-1 ${c}`}>{value}</p>
      {hint && <p className="text-[11px] text-brand-green/40 mt-0.5">{hint}</p>}
    </div>
  );
};

function computeSim(f) {
    const invest = N(f.prix) + N(f.frais) + N(f.travaux);
    const emprunt = Math.max(0, invest - N(f.apport));
    const nMois = N(f.duree) * 12;
    const tm = N(f.taux) / 100 / 12;
    const mensualiteCapital = tm > 0 && nMois > 0 ? emprunt * tm / (1 - Math.pow(1 + tm, -nMois)) : (nMois > 0 ? emprunt / nMois : 0);
    const assuranceMensuelle = emprunt * (N(f.assurance) / 100) / 12;
    const mensualite = mensualiteCapital + assuranceMensuelle;
    const loyerAn = N(f.loyer) * 12;
    const chargesAn = N(f.taxe) + N(f.assurances_pno) + N(f.charges);
    const brut = N(f.prix) > 0 ? loyerAn / (N(f.prix) + N(f.frais) + N(f.travaux)) * 100 : 0;
    const net = invest > 0 ? (loyerAn - chargesAn) / invest * 100 : 0;
    const cashflowAn = loyerAn - chargesAn - mensualite * 12;
    const netNet = invest > 0 ? cashflowAn / invest * 100 : 0;
    const cashMois = cashflowAn / 12;
    const loyerEquilibre = mensualite + chargesAn / 12;
    const ltv = N(f.prix) > 0 ? emprunt / N(f.prix) * 100 : 0;
    // Score de risque 0-100 (plus bas = mieux) → converti en note /100 (plus haut = mieux)
    let risk = 50;
    risk -= cashMois >= 0 ? 20 : -20;
    risk -= brut >= 6 ? 15 : brut >= 4 ? 5 : -10;
    risk += ltv > 90 ? 15 : ltv > 80 ? 5 : -5;
    const score = Math.max(0, Math.min(100, 100 - risk));

    // Trajectoire du cashflow cumulé + patrimoine net (cashflow cumulé + capital de prêt remboursé)
    const years = Math.max(1, Math.round(N(f.duree)));
    const horizon = years + 10;
    const postLoanAn = loyerAn - chargesAn; // cashflow annuel une fois le crédit soldé
    let balance = emprunt;           // capital restant dû
    let principalPaid = 0;           // capital remboursé cumulé (= patrimoine bâti par le prêt)
    let cum = -N(f.apport);
    const series = [{ year: 0, cum: Math.round(cum), patrimoine: Math.round(cum) }];
    let breakEven = null;
    let patrimoineBreakEven = null;
    for (let y = 1; y <= horizon; y++) {
      if (y <= years) {
        for (let m = 0; m < 12; m++) {
          const interest = balance * tm;
          const principal = Math.min(balance, Math.max(0, mensualiteCapital - interest));
          balance -= principal;
          principalPaid += principal;
        }
        cum += cashflowAn;
      } else {
        cum += postLoanAn;
      }
      const patrimoine = cum + principalPaid;
      series.push({ year: y, cum: Math.round(cum), patrimoine: Math.round(patrimoine) });
      if (breakEven === null && cum >= 0) breakEven = y;
      if (patrimoineBreakEven === null && patrimoine >= 0) patrimoineBreakEven = y;
    }
    const capitalRembourse = Math.round(principalPaid);
    return { invest, emprunt, mensualite, brut, net, netNet, cashflowAn, cashMois, loyerEquilibre, ltv, score, series, breakEven, patrimoineBreakEven, loanEnd: years, postLoanAn: Math.round(postLoanAn), postLoanMois: Math.round(postLoanAn / 12), capitalRembourse };
}

export default function Simulateur() {
  const [name, setName] = useState("Nouveau projet");
  const [f, setF] = useState({
    prix: 200000, frais: 16000, travaux: 0, apport: 20000, duree: 20,
    taux: 3.5, assurance: 0.34, loyer: 900, taxe: 1200, assurances_pno: 180, charges: 600,
  });
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));

  const [scenarios, setScenarios] = useState([]);
  const [currentId, setCurrentId] = useState(null);
  const [showLoad, setShowLoad] = useState(false);
  const [saving, setSaving] = useState(false);
  const [compareIds, setCompareIds] = useState([]);
  const [showCompare, setShowCompare] = useState(false);

  // Pré-remplissage depuis une annonce "Bons deals" (navigation state)
  const location = useLocation();
  useEffect(() => {
    const incoming = location.state?.sim;
    if (incoming) {
      setF((s) => ({ ...s, ...Object.fromEntries(Object.entries(incoming).map(([k, v]) => [k, Number(v) || 0])) }));
      if (location.state?.name) setName(location.state.name);
      setCurrentId(null);
      toast.success("Données du deal reprises dans le simulateur");
      window.history.replaceState({}, document.title);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const r = useMemo(() => computeSim(f), [f]);

  const scoreTone = r.score >= 66 ? "up" : r.score >= 40 ? "neutral" : "down";
  const scoreLabel = r.score >= 66 ? "Risque faible" : r.score >= 40 ? "Risque modéré" : "Risque élevé";

  const [pdfBusy, setPdfBusy] = useState(false);
  const exportPdf = async () => {
    setPdfBusy(true);
    try {
      const payload = {
        name, inputs: f,
        results: {
          invest: r.invest, emprunt: r.emprunt, mensualite: r.mensualite,
          brut: r.brut, net: r.net, net_net: r.netNet, ltv: r.ltv,
          cash_mois: r.cashMois, cashflow_an: r.cashflowAn, loyer_equilibre: r.loyerEquilibre,
          score: r.score, score_label: scoreLabel,
          break_even: r.breakEven, loan_end: r.loanEnd, post_loan_an: r.postLoanAn,
          capital_rembourse: r.capitalRembourse, patrimoine_break_even: r.patrimoineBreakEven,
        },
        series: r.series,
      };
      const res = await api.post("/sim/pdf", payload, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `simulation-${(name || "projet").replace(/[^a-z0-9]+/gi, "-").toLowerCase()}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Fiche PDF téléchargée");
    } catch (e) { toast.error("Export impossible"); }
    finally { setPdfBusy(false); }
  };

  const loadScenarios = async () => {
    try {
      const { data } = await api.get("/sim/scenarios");
      setScenarios(data || []);
    } catch (e) { /* silencieux */ }
  };
  useEffect(() => { loadScenarios(); }, []);

  const saveScenario = async () => {
    const nm = (name || "").trim();
    if (!nm) { toast.error("Donnez un nom au projet"); return; }
    setSaving(true);
    const payload = { name: nm, inputs: f, results: r };
    try {
      if (currentId) {
        await api.put(`/sim/scenarios/${currentId}`, payload);
        toast.success("Scénario mis à jour");
      } else {
        const { data } = await api.post("/sim/scenarios", payload);
        setCurrentId(data.id);
        toast.success("Scénario enregistré");
      }
      loadScenarios();
    } catch (e) {
      toast.error("Échec de l'enregistrement");
    } finally { setSaving(false); }
  };

  const applyScenario = (s) => {
    setName(s.name || "Projet");
    setF({ ...f, ...(s.inputs || {}) });
    setCurrentId(s.id);
    setShowLoad(false);
    toast.success(`« ${s.name} » chargé`);
  };

  const removeScenario = async (id, e) => {
    e.stopPropagation();
    try {
      await api.delete(`/sim/scenarios/${id}`);
      if (id === currentId) setCurrentId(null);
      setScenarios((list) => list.filter((x) => x.id !== id));
      toast.success("Scénario supprimé");
    } catch (err) { toast.error("Échec de la suppression"); }
  };

  const newScenario = () => {
    setCurrentId(null);
    setName("Nouveau projet");
    setF({ prix: 200000, frais: 16000, travaux: 0, apport: 20000, duree: 20, taux: 3.5, assurance: 0.34, loyer: 900, taxe: 1200, assurances_pno: 180, charges: 600 });
    toast.success("Nouveau projet");
  };

  return (
    <div className="space-y-6" data-testid="simulateur-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-2xl bg-brand-lime/30 text-brand-greenDeep flex items-center justify-center"><Calculator className="w-5 h-5" /></div>
          <div>
            <input value={name} onChange={(e) => setName(e.target.value)} className="font-display text-2xl font-semibold text-brand-greenDeep bg-transparent outline-none border-b border-transparent focus:border-brand-soft" data-testid="sim-name" />
            <p className="text-sm text-brand-green/55">Simulez la rentabilité de votre investissement locatif</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={newScenario} className="flex items-center gap-1.5 px-3 py-2 rounded-xl border border-brand-soft text-sm text-brand-greenDeep hover:bg-brand-softer transition-colors" data-testid="sim-new-btn">
            <Plus className="w-4 h-4" /> Nouveau
          </button>
          <button onClick={() => setShowLoad((v) => !v)} className="flex items-center gap-1.5 px-3 py-2 rounded-xl border border-brand-soft text-sm text-brand-greenDeep hover:bg-brand-softer transition-colors" data-testid="sim-load-btn">
            <FolderOpen className="w-4 h-4" /> Mes scénarios{scenarios.length ? ` (${scenarios.length})` : ""}
          </button>
          <button onClick={saveScenario} disabled={saving} className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-brand-greenDeep text-white text-sm font-medium hover:bg-brand-green transition-colors disabled:opacity-60" data-testid="sim-save-btn">
            <Save className="w-4 h-4" /> {currentId ? "Mettre à jour" : "Enregistrer"}
          </button>
        </div>
      </div>

      {showLoad && (
        <div className="bg-white rounded-2xl border border-brand-soft p-4" data-testid="sim-scenarios-panel">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-semibold text-brand-greenDeep">Scénarios enregistrés</h4>
            <div className="flex items-center gap-2">
              <button onClick={() => { if (compareIds.length < 2) { toast.error("Sélectionnez 2 à 3 scénarios (cochez les cases)"); return; } setShowCompare(true); }} disabled={compareIds.length < 2} className="text-xs font-semibold px-3 py-1.5 rounded-full bg-brand-lime hover:bg-brand-limeDark text-brand-greenDeep disabled:opacity-40" data-testid="sim-compare-btn">Comparer ({compareIds.length})</button>
              <button onClick={() => setShowLoad(false)} className="text-brand-green/50 hover:text-brand-greenDeep"><X className="w-4 h-4" /></button>
            </div>
          </div>
          {scenarios.length === 0 ? (
            <p className="text-sm text-brand-green/50 py-3">Aucun scénario enregistré pour l'instant.</p>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {scenarios.map((s) => {
                const picked = compareIds.includes(s.id);
                return (
                <div key={s.id} className={`rounded-xl border p-3 transition-colors ${s.id === currentId ? "border-brand-lime bg-brand-lime/10" : picked ? "border-brand-limeDark" : "border-brand-soft"}`} data-testid={`sim-scenario-${s.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <label className="flex items-start gap-2 min-w-0 flex-1 cursor-pointer">
                      <input type="checkbox" checked={picked} onChange={(e) => { e.stopPropagation(); setCompareIds((ids) => picked ? ids.filter((x) => x !== s.id) : (ids.length >= 3 ? ids : [...ids, s.id])); }} className="mt-0.5 w-4 h-4 accent-brand-limeDark shrink-0" data-testid={`sim-compare-check-${s.id}`} />
                      <span className="min-w-0" onClick={(e) => { e.preventDefault(); applyScenario(s); }}>
                        <span className="block text-sm font-medium text-brand-greenDeep truncate">{s.name}</span>
                        <span className="block text-xs text-brand-green/50 mt-0.5">{eur(s.inputs?.prix)} · {N(computeSim(s.inputs || {}).brut).toFixed(1)} % brut</span>
                      </span>
                    </label>
                    <button onClick={(e) => removeScenario(s.id, e)} className="text-brand-green/40 hover:text-red-600 shrink-0" data-testid={`sim-delete-${s.id}`}><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
                );
              })}
            </div>
          )}
          <p className="text-xs text-brand-green/40 mt-2">Cochez 2 à 3 scénarios puis cliquez « Comparer » pour choisir le meilleur investissement.</p>
        </div>
      )}

      {showCompare && (() => {
        const cols = scenarios.filter((s) => compareIds.includes(s.id)).map((s) => ({ name: s.name, r: computeSim(s.inputs || {}) }));
        const rows = [
          { label: "Prix d'acquisition", get: (r, s) => eur(s.inputs?.prix), raw: (r, s) => N(s.inputs?.prix), best: "min" },
          { label: "Investissement total", get: (r) => eur(r.invest) },
          { label: "Mensualité de prêt", get: (r) => eur(r.mensualite) },
          { label: "Rendement brut", get: (r) => pct(r.brut), raw: (r) => r.brut, best: "max" },
          { label: "Rendement net", get: (r) => pct(r.net), raw: (r) => r.net, best: "max" },
          { label: "Rendement net-net", get: (r) => pct(r.netNet), raw: (r) => r.netNet, best: "max" },
          { label: "Cashflow mensuel", get: (r) => eur(r.cashMois), raw: (r) => r.cashMois, best: "max" },
          { label: "Quotité (LTV)", get: (r) => pct(r.ltv), raw: (r) => r.ltv, best: "min" },
          { label: "Score de fiabilité", get: (r) => `${r.score}/100`, raw: (r) => r.score, best: "max" },
          { label: "Rentable dès", get: (r) => r.breakEven ? `Année ${r.breakEven}` : "—", raw: (r) => r.breakEven || 999, best: "min" },
          { label: "Patrimoine net à terme", get: (r) => eur(r.series[r.series.length - 1].patrimoine), raw: (r) => r.series[r.series.length - 1].patrimoine, best: "max" },
        ];
        const scenarios2 = scenarios.filter((s) => compareIds.includes(s.id));
        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={() => setShowCompare(false)}>
            <div className="bg-white rounded-3xl p-6 w-full max-w-3xl max-h-[88vh] overflow-auto" onClick={(e) => e.stopPropagation()} data-testid="sim-compare-modal">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-display text-xl font-semibold text-brand-greenDeep">Comparaison d'investissements</h3>
                <button onClick={() => setShowCompare(false)} className="text-brand-green/50 hover:text-brand-green"><X className="w-5 h-5" /></button>
              </div>
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr>
                    <th className="text-left p-2 text-brand-green/50 font-medium"></th>
                    {cols.map((c, i) => <th key={i} className="text-right p-2 text-brand-greenDeep font-semibold max-w-[140px] truncate">{c.name}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, ri) => {
                    let bestIdx = -1;
                    if (row.raw && row.best) {
                      const vals = cols.map((c, i) => row.raw(c.r, scenarios2[i]));
                      const target = row.best === "max" ? Math.max(...vals) : Math.min(...vals);
                      bestIdx = vals.indexOf(target);
                    }
                    return (
                      <tr key={ri} className="border-t border-brand-soft">
                        <td className="p-2 text-brand-green/60">{row.label}</td>
                        {cols.map((c, i) => (
                          <td key={i} className={`p-2 text-right font-medium ${i === bestIdx ? "text-emerald-600 font-bold" : "text-brand-greenDeep"}`}>
                            {row.get(c.r, scenarios2[i])}{i === bestIdx && row.best ? " ★" : ""}
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <p className="text-xs text-brand-green/40 mt-3">★ = meilleure valeur sur la ligne. Les indicateurs sont recalculés en direct à partir des données de chaque scénario.</p>
            </div>
          </div>
        );
      })()}

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Inputs */}
        <div className="bg-white rounded-3xl border border-brand-soft p-6 space-y-5">
          <div>
            <h3 className="font-semibold text-brand-greenDeep mb-3">Investissement</h3>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Prix d'acquisition" value={f.prix} onChange={set("prix")} />
              <Field label="Frais d'acquisition (notaire)" value={f.frais} onChange={set("frais")} />
              <Field label="Travaux" value={f.travaux} onChange={set("travaux")} />
              <Field label="Apport personnel" value={f.apport} onChange={set("apport")} />
            </div>
          </div>
          <div>
            <h3 className="font-semibold text-brand-greenDeep mb-3">Financement</h3>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Durée du prêt" value={f.duree} onChange={set("duree")} suffix="ans" />
              <Field label="Taux d'intérêt" value={f.taux} onChange={set("taux")} suffix="%" />
              <Field label="Taux assurance emprunteur" value={f.assurance} onChange={set("assurance")} suffix="%" />
            </div>
          </div>
          <div>
            <h3 className="font-semibold text-brand-greenDeep mb-3">Exploitation</h3>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Loyer hors charges / mois" value={f.loyer} onChange={set("loyer")} />
              <Field label="Taxe foncière / an" value={f.taxe} onChange={set("taxe")} />
              <Field label="Assurances (PNO) / an" value={f.assurances_pno} onChange={set("assurances_pno")} />
              <Field label="Autres charges / an" value={f.charges} onChange={set("charges")} />
            </div>
          </div>
        </div>

        {/* Résultats */}
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Kpi label="Investissement total" value={eur(r.invest)} icon={Euro} hint={`Emprunt ${eur(r.emprunt)}`} formula="Investissement total = Prix + Frais d'acquisition + Travaux." />
            <Kpi label="Mensualité de prêt" value={eur(r.mensualite)} icon={Wallet} hint="assurance incluse" formula="Mensualité = remboursement du capital emprunté (amortissement au taux, sur la durée) + assurance emprunteur mensuelle." />
            <Kpi label="Rendement brut" value={pct(r.brut)} icon={Percent} tone={r.brut >= 5 ? "up" : "neutral"} formula="Rendement brut = Loyer annuel ÷ coût total (prix + frais + travaux) × 100." />
            <Kpi label="Rendement net" value={pct(r.net)} icon={Percent} tone={r.net >= 3 ? "up" : "neutral"} formula="Rendement net = (Loyer annuel − charges annuelles : taxe foncière + assurance PNO + charges) ÷ coût total × 100." />
            <Kpi label="Rendement net-net" value={pct(r.netNet)} icon={Percent} tone={r.netNet >= 0 ? "up" : "down"} hint="après charges & crédit" formula="Rendement net-net = Cashflow annuel (loyer − charges − mensualités de crédit) ÷ coût total × 100." />
            <Kpi label="Quotité (LTV)" value={pct(r.ltv)} icon={Gauge} formula="Quotité de financement (LTV) = Montant emprunté ÷ Prix du bien × 100." />
            <Kpi label="Cashflow mensuel" value={eur(r.cashMois)} icon={r.cashMois >= 0 ? TrendingUp : TrendingDown} tone={r.cashMois >= 0 ? "up" : "down"} formula="Cashflow mensuel = (Loyer annuel − charges annuelles − mensualités de crédit) ÷ 12." />
            <Kpi label="Loyer d'équilibre" value={eur(r.loyerEquilibre)} icon={Euro} hint="loyer min. pour couvrir" formula="Loyer d'équilibre = Mensualité de crédit + charges mensuelles (loyer minimum pour couvrir toutes les dépenses)." />
          </div>
          <div className={`rounded-3xl border p-5 ${scoreTone === "up" ? "bg-emerald-50 border-emerald-200" : scoreTone === "down" ? "bg-red-50 border-red-200" : "bg-brand-softer border-brand-soft"}`} data-testid="risk-score">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-brand-green/60">Score de risque</p>
                <p className={`font-display text-3xl font-bold ${scoreTone === "up" ? "text-emerald-600" : scoreTone === "down" ? "text-red-600" : "text-brand-greenDeep"}`}>{Math.round(r.score)}/100</p>
              </div>
              <span className={`text-sm font-bold px-4 py-2 rounded-full ${scoreTone === "up" ? "bg-emerald-100 text-emerald-700" : scoreTone === "down" ? "bg-red-100 text-red-700" : "bg-brand-soft text-brand-greenDeep"}`}>{scoreLabel}</span>
            </div>
            <div className="mt-3 h-2 rounded-full bg-white/70 overflow-hidden">
              <div className={`h-full ${scoreTone === "up" ? "bg-emerald-500" : scoreTone === "down" ? "bg-red-500" : "bg-brand-lime"}`} style={{ width: `${Math.round(r.score)}%` }} />
            </div>
            <p className="text-xs text-brand-green/50 mt-3">Estimation indicative basée sur le cashflow, le rendement brut et la quotité de financement. Ne constitue pas un conseil financier.</p>
          </div>

          {/* Cashflow cumulé sur la durée du prêt et au-delà */}
          <div className="bg-white rounded-3xl border border-brand-soft p-5" data-testid="cashflow-chart">
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-2">
                <LineChartIcon className="w-4 h-4 text-brand-greenDeep" />
                <h3 className="font-semibold text-brand-greenDeep">Cashflow cumulé</h3>
              </div>
              {r.breakEven ? (
                <span className="text-xs font-semibold px-3 py-1 rounded-full bg-emerald-100 text-emerald-700" data-testid="breakeven-badge">Rentable dès l'année {r.breakEven}</span>
              ) : (
                <span className="text-xs font-semibold px-3 py-1 rounded-full bg-amber-100 text-amber-700" data-testid="breakeven-badge">Pas de retour sur l'horizon affiché</span>
              )}
            </div>
            <p className="text-xs text-brand-green/50 mb-3">Trésorerie cumulée en partant de votre apport ({eur(-r.series[0].cum)}). Le <b>patrimoine net</b> ajoute le capital de prêt remboursé ({eur(r.capitalRembourse)} à terme) : même avec un cashflow négatif, vous vous enrichissez. Après la fin du prêt (année {r.loanEnd}), le cashflow annuel passe à <b>{eur(r.postLoanAn)}</b>.</p>
            <div className="h-56 -ml-2">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={r.series} margin={{ top: 5, right: 8, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="cfGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#bfd94f" stopOpacity={0.55} />
                      <stop offset="100%" stopColor="#bfd94f" stopOpacity={0.05} />
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="year" tick={{ fontSize: 10, fill: "#5b6b53" }} tickFormatter={(y) => `A${y}`} interval={1} />
                  <YAxis tick={{ fontSize: 11, fill: "#5b6b53" }} width={54} tickFormatter={(v) => `${Math.round(v / 1000)}k`} />
                  <Tooltip
                    formatter={(v, n) => [`${Number(v).toLocaleString("fr-FR")} €`, n === "cum" ? "Cashflow cumulé" : "Patrimoine net"]}
                    labelFormatter={(y) => `Année ${y}`}
                    contentStyle={{ borderRadius: 12, border: "1px solid #e6e2d3", fontSize: 12 }}
                  />
                  <Legend formatter={(v) => v === "cum" ? "Cashflow cumulé" : "Patrimoine net (avec capital remboursé)"} wrapperStyle={{ fontSize: 11 }} />
                  <ReferenceLine y={0} stroke="#94a389" strokeDasharray="4 4" />
                  <ReferenceLine x={r.loanEnd} stroke="#c9a24b" strokeDasharray="3 3" label={{ value: "Fin du prêt", position: "insideTopLeft", fontSize: 10, fill: "#a4801f" }} />
                  {r.breakEven && <ReferenceLine x={r.breakEven} stroke="#059669" strokeDasharray="3 3" label={{ value: "Rentable", position: "insideTopRight", fontSize: 10, fill: "#059669" }} />}
                  <Area type="monotone" dataKey="cum" stroke="#3a4a32" strokeWidth={2} fill="url(#cfGrad)" />
                  <Line type="monotone" dataKey="patrimoine" stroke="#c9a24b" strokeWidth={2.2} dot={false} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
            <button onClick={exportPdf} disabled={pdfBusy} className="w-full mt-4 py-2.5 rounded-full bg-brand-greenDeep hover:bg-brand-green text-white font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60" data-testid="sim-pdf-btn">
              {pdfBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />} Exporter la fiche PDF pour la banque
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
